import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import GradientIcon from 'components/ui/GradientIcon';
import ContactMethodModal from 'components/modals/ContactMethodModal';

const ProcessSection = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Touch/swipe functionality for mobile cards
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showSwipeHints, setShowSwipeHints] = useState(true);
  const cardsContainerRef = useRef(null);

  const handleContactMethod = (method) => {
    if (method === 'whatsapp') {
      const phoneNumber = '254793863320';
      const message = encodeURIComponent('Hi! I want to get started with my assignment. Can you help me with the process?');
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    } else if (method === 'discord') {
      const discordInvite = 'https://discord.gg/yourdiscordinvite';
      window.open(discordInvite, '_blank');
    }
  };

  // Swipe detection
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentCardIndex < processSteps.length - 1) {
      // Swipe left - next card
      setCurrentCardIndex(prev => prev + 1);
      setShowSwipeHints(false);
    } else if (isRightSwipe && currentCardIndex > 0) {
      // Swipe right - previous card
      setCurrentCardIndex(prev => prev - 1);
      setShowSwipeHints(false);
    }
  };

  // Auto-hide swipe hints after 5 seconds
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile && showSwipeHints) {
      const timer = setTimeout(() => {
        setShowSwipeHints(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSwipeHints]);

  const handleSubmitEmail = (formData) => {
    // In a real implementation, you would send this data to your backend
    console.log('Email form submitted:', formData);

    // For demonstration purposes, we'll just show an alert
    alert(`Thank you for your message! We'll get back to you at ${formData.email} as soon as possible.`);

    // Close the modal and reset the form
    setShowEmailModal(false);
    setEmailFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };
  const processSteps = [
    {
      step: 1,
      title: "Submit Your Assignment",
      description: "Share your requirements through our simple form or WhatsApp",
      details: [
        "Upload assignment instructions and files",
        "Specify deadline and academic level",
        "Choose your preferred citation style",
        "Get instant price calculation"
      ],
      icon: "Upload",
      time: "2 minutes",
      color: "primary",
      gradient: "primary"
    },
    {
      step: 2,
      title: "Expert Assignment",
      description: "Get matched with the perfect expert for your subject",
      details: [
        "AI-powered expert matching system",
        "Subject specialization verification",
        "Academic level compatibility check",
        "Availability and deadline confirmation"
      ],
      icon: "UserCheck",
      time: "5 minutes",
      color: "secondary",
      gradient: "ocean"
    },
    {
      step: 3,
      title: "Work in Progress",
      description: "Your expert begins working on your assignment immediately",
      details: [
        "Real-time progress updates via WhatsApp",
        "Direct communication with your expert",
        "Quality checkpoints throughout process",
        "Preliminary draft review available"
      ],
      icon: "Edit",
      time: "Ongoing",
      color: "accent",
      gradient: "violet"
    },
    {
      step: 4,
      title: "Quality Review",
      description: "Comprehensive quality check before delivery",
      details: [
        "Multi-level editorial review",
        "Plagiarism detection scan",
        "Format and citation verification",
        "Academic standards compliance check"
      ],
      icon: "Search",
      time: "30 minutes",
      color: "warning",
      gradient: "gold"
    },
    {
      step: 5,
      title: "Delivery & Support",
      description: "Receive your completed assignment with full support",
      details: [
        "On-time delivery guarantee",
        "Comprehensive plagiarism report",
        "Free revisions if needed",
        "24/7 post-delivery support"
      ],
      icon: "CheckCircle",
      time: "On deadline",
      color: "success",
      gradient: "emerald"
    }
  ];

  const supportChannels = [
    {
      platform: "WhatsApp",
      description: "Instant messaging with 2-minute response time",
      features: ["Real-time updates", "File sharing", "Voice messages", "24/7 availability"],
      icon: "MessageSquare",
      color: "green-500",
      action: {
        type: "link",
        url: "https://wa.me/254793863320",
        label: "Chat on WhatsApp"
      }
    },
    {
      platform: "Discord",
      description: "Join our student community for peer support",
      features: ["Study groups", "Expert Q&A", "Resource sharing", "Academic discussions"],
      icon: "Discord", // Special case for Discord icon
      color: "#5865F2", // Discord brand color
      action: {
        type: "link",
        url: "https://discord.gg/yourdiscordinvite",
        label: "Join Discord Server",
        style: "discord"
      }
    },
    {
      platform: "Email",
      description: "Formal communication and document sharing",
      features: ["Detailed explanations", "File attachments", "Progress reports", "Official receipts"],
      icon: "Mail",
      color: "secondary",
      action: {
        type: "modal",
        label: "Send Email"
      }
    }
  ];

  return (
    <section className="process-section py-20 bg-grid-pattern">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Our <span className="text-gradient">Step-by-Step</span> Process
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            From submission to delivery, every step is designed for your convenience and peace of mind. 
            Here's exactly what happens when you choose <span className = 'text-accent' >Somo. </span>
          </p>
        </motion.div>

        {/* Process Timeline */}
        <div className="relative mb-20">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-secondary to-success rounded-full hidden lg:block"></div>

          {/* Mobile: Swipe container with hints */}
          <div className="lg:hidden relative">
            {/* Pulsating arrow hints */}
            {showSwipeHints && (
              <div className="absolute top-1/2 left-4 right-4 flex justify-between z-10 pointer-events-none">
                <motion.div
                  animate={{ 
                    x: [-8, 8, -8],
                    opacity: [0.6, 1, 0.6]
                  }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 2,
                    ease: "easeInOut"
                  }}
                  className="bg-white/90 rounded-full p-1 shadow-lg"
                >
                  <Icon name="ChevronLeft" size={24} className="text-primary" />
                </motion.div>
                <motion.div
                  animate={{ 
                    x: [8, -8, 8],
                    opacity: [0.6, 1, 0.6]
                  }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 2,
                    ease: "easeInOut"
                  }}
                  className="bg-white/90 rounded-full p-1 shadow-lg"
                >
                  <Icon name="ChevronRight" size={24} className="text-primary" />
                </motion.div>
              </div>
            )}

            {/* Mobile swipe indicator */}
            <div className="flex justify-center mb-4 space-x-2">
              {processSteps.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentCardIndex ? 'bg-primary w-8' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Mobile card container */}
            <div 
              ref={cardsContainerRef}
              className="overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <motion.div
                className="flex"
                animate={{ x: `${-currentCardIndex * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {processSteps.map((step, index) => (
                  <div key={step.step} className="w-full flex-shrink-0 px-4">
                    <div className="glass-card p-6 hover:transform hover:-translate-y-2 transition-all duration-250 rounded-2xl">
                      <div className="flex items-center space-x-4 mb-4">
                        <GradientIcon
                          name={step.icon}
                          size={24}
                          gradient={step.gradient}
                          containerClassName="w-12 h-12 rounded-full"
                        />
                        <div>
                          <h3 className="text-xl font-semibold text-text-primary">
                            Step {step.step}: {step.title}
                          </h3>
                          <div className={`text-sm text-${step.color} font-medium`}>
                            {step.time}
                          </div>
                        </div>
                      </div>

                      <p className="text-text-secondary mb-4">
                        {step.description}
                      </p>

                      <ul className="space-y-2">
                        {step.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className={`text-${step.color} mt-1 flex-shrink-0`} />
                            <span className="text-text-secondary text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Desktop: Original timeline layout */}
          <div className="hidden lg:block">
            {processSteps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative flex items-center mb-16 ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              }`}
            >
              {/* Step Content */}
              <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'}`}>
                <div 
                  className="glass-card p-6 hover:transform hover:-translate-y-2 transition-all duration-250 rounded-2xl"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <GradientIcon
                      name={step.icon}
                      size={24}
                      gradient={step.gradient}
                      containerClassName="w-12 h-12 rounded-full"
                    />
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary">
                        {step.title}
                      </h3>
                      <div className={`text-sm text-${step.color} font-medium`}>
                        {step.time}
                      </div>
                    </div>
                  </div>

                  <p className="text-text-secondary mb-4">
                    {step.description}
                  </p>

                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start space-x-2">
                        <Icon name="Check" size={16} className={`text-${step.color} mt-1 flex-shrink-0`} />
                        <span className="text-text-secondary text-sm">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Step Number (Center) */}
              <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 z-10">
                <GradientIcon
                  name={step.icon}
                  size={24}
                  gradient={step.gradient}
                  containerClassName="w-16 h-16 rounded-full border-4 border-background"
                />
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold z-20">
                  {step.step}
                </div>
              </div>

              {/* Mobile Step Number */}
              <div className="lg:hidden absolute -left-4 top-6">
                <GradientIcon
                  name={step.icon}
                  size={16}
                  gradient={step.gradient}
                  containerClassName="w-8 h-8 rounded-full"
                />
              </div>

              {/* Spacer for opposite side */}
              <div className="hidden lg:block w-5/12"></div>
            </motion.div>
            ))}
          </div>
        </div>
        {/* Process Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center glass-card p-8 rounded-2xl"
        >
          <GradientIcon name="Shield" size={48} gradient="success" containerClassName="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Process Guarantee
          </h3>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-6">
            Every step of our process is backed by our satisfaction guarantee. 
            If any part of the process doesn't meet your expectations, we'll make it right 
            or provide a full refund.
          </p>
          <div className="flex flex-wrap justify-center items-center space-x-6 text-sm text-text-muted">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} className="text-primary" />
              <span>On-Time Delivery</span>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="RefreshCw" size={16} className="text-secondary" />
              <span>Free Revisions</span>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="DollarSign" size={16} className="text-accent" />
              <span>Money-Back Guarantee</span>
            </div>
          </div>
        </motion.div>

        {/* Ready to Start CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl border border-primary/20"
        >
          <GradientIcon name="Rocket" size={48} gradient="primary" containerClassName="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Ready to Start Your Success Journey?
          </h3>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-6">
            Join thousands of students who trust our proven process. 
            Get started in less than 2 minutes and see the difference quality makes.
          </p>
          <button
            onClick={() => setShowContactModal(true)}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center space-x-2"
          >
            <GradientIcon name="MessageCircle" size={24} gradient="secondary" />
            <span>Start Your Assignment</span>
          </button>
        </motion.div>
      </div>

      {/* Contact Method Modal */}
      <ContactMethodModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
        onSelectMethod={handleContactMethod}
        title="How would you like to get started?"
      />

      {/* Email Modal */}
      <EmailModal 
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        formData={emailFormData}
        setFormData={setEmailFormData}
        onSubmit={handleSubmitEmail}
      />
    </section>
  );
};

// Email Modal Component
const EmailModal = ({ isOpen, onClose, formData, setFormData, onSubmit }) => {
  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="glass-card rounded-xl shadow-xl max-w-md w-full p-6 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <Icon name="X" size={24} />
        </button>

        <h3 className="text-2xl font-bold text-text-primary mb-2">Send Us an Email</h3>
        <p className="text-text-secondary mb-6">Fill out this form and we'll get back to you as soon as possible.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-1">Your Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-secondary/50 focus:border-secondary"
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-1">Your Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-secondary/50 focus:border-secondary"
              placeholder="john.doe@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-text-primary mb-1">Subject</label>
            <input
              id="subject"
              name="subject"
              type="text"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-secondary/50 focus:border-secondary"
              placeholder="Assignment Help Request"
              required
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-1">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:ring-4 focus:ring-secondary/50 focus:border-secondary"
              placeholder="Please describe how we can help you..."
              required
            />
          </div>

          <div className="pt-4">
            <button 
              type="submit"
              className="w-full bg-secondary hover:bg-secondary/90 text-white py-3 px-4 rounded-lg font-medium transition-colors"
            >
              <div className="flex items-center justify-center space-x-2">
                <Icon name="Send" size={16} />
                <span>Send Message</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcessSection;
